/**
 * PersonnelAppointment 타입 정의
 *
 * 인사발령 이력 데이터 타입입니다.
 * 직원의 직위 변경 이력을 관리합니다.
 *
 * **용도:**
 * - 직원의 승진/강등/조직이동 이력 관리
 * - 현재 직위(positionId) 및 직위 발령일(currentPositionStartDate)의 원천 데이터
 * - 진급 후보 산출 시 정확한 직위 연차 계산 기반
 *
 * **데이터 소스:**
 * - Firestore `personnelAppointments` 컬렉션
 */

import { DocumentData, QueryDocumentSnapshot, Timestamp } from 'firebase/firestore';

/**
 * 인사발령 유형
 *
 * - promotion: 승진 (상위 직위로 변경)
 * - demotion: 강등 (하위 직위로 변경)
 * - transfer: 조직 이동 (조직 관련 발령, 향후 구현)
 * - initial: 시스템 도입 시 최초 입력용 (초기 세팅)
 * - correction: 과거 발령일/직위 수정용
 */
export type PersonnelAppointmentType =
  | 'promotion'
  | 'demotion'
  | 'transfer'
  | 'initial'
  | 'correction';

/**
 * V72: 인사발령 유형 (신규 구조)
 *
 * - EARLY_PROMOTION: 조기 진급 발령
 * - POSITION_APPOINTMENT: 직위 임명 발령
 * - TITLE_APPOINTMENT: 직책 임명 발령
 */
export type AppointmentType =
  | 'EARLY_PROMOTION'
  | 'PROMOTION'
  | 'POSITION_APPOINTMENT'
  | 'TITLE_APPOINTMENT';

/**
 * V72: 인사발령 상태
 *
 * - PLANNED: 예약됨 (미래 effectiveDate, 스케줄러 대기)
 * - EXECUTED: 실행됨 (users 문서 반영 완료)
 * - CANCELED: 취소됨 (V73, 스케줄러 제외)
 */
export type AppointmentStatus =
  | 'PLANNED'
  | 'EXECUTED'
  | 'CANCELED';

/**
 * 인사발령(PersonnelAppointment) 타입
 *
 * UI에서 필요한 인사발령 정보를 담는 인터페이스입니다.
 */
export interface PersonnelAppointment {
  // 필수 필드
  /** Firestore 문서 ID */
  id: string;

  /** 대상 직원 ID (users 컬렉션 참조) */
  employeeId: string;

  /** V72: 직원명 (denormalized) */
  employeeName?: string;

  /** V93: 사번 (조인 필드) */
  employeeNumber?: string;

  /** V93: 소속 조직명 (조인 필드) */
  orgUnitName?: string;

  /** V93: human-readable 변경 내용 (예: "대리 → 과장") */
  changeDescription?: string;

  // 발령 정보
  /**
   * 변경 전 직위 ID (positionLevels 컬렉션 참조)
   *
   * 첫 발령(신입 입사, 경력 입사 등)인 경우 null 가능
   */
  fromPositionId?: string | null;

  /**
   * 변경 후 직위 ID (positionLevels 컬렉션 참조)
   *
   * 필수 필드입니다.
   */
  toPositionId?: string;

  /** V72: 변경 전 직위명 (denormalized) */
  fromPositionName?: string | null;

  /** V72: 변경 후 직위명 (denormalized) */
  toPositionName?: string;

  /** V72: 변경 전 직책 ID (jobTitles 컬렉션 참조) */
  fromTitleId?: string | null;

  /** V72: 변경 후 직책 ID (jobTitles 컬렉션 참조) */
  toTitleId?: string;

  /** V72: 변경 전 직책명 (denormalized) */
  fromTitleName?: string | null;

  /** V72: 변경 후 직책명 (denormalized) */
  toTitleName?: string;

  /**
   * 발령일 (YYYY-MM-DD)
   *
   * 해당 직위로 발령된 날짜입니다.
   * 이 날짜를 기준으로 직위 연차를 계산합니다.
   */
  effectiveDate: string;

  /**
   * 발령 유형 (Legacy)
   *
   * - promotion: 승진
   * - demotion: 강등
   * - transfer: 조직 이동 (향후 구현)
   * - initial: 시스템 도입 시 최초 입력
   * - correction: 과거 발령 정보 수정
   */
  type?: PersonnelAppointmentType;

  /** V72: 임명 유형 (신규) */
  appointmentType?: AppointmentType;

  /** V72: 발령 상태 */
  status?: AppointmentStatus;

  /** V72: 실행 여부 */
  isExecuted?: boolean;

  /**
   * 발령 설명/메모 (선택)
   *
   * 발령 사유, 특이사항 등을 기록합니다.
   */
  note?: string;

  /** V72: 발령 사유 */
  reason?: string;

  // 진급 가산 정보 (V61+)
  /** 경력 입사/전환 입사 가산 연수 */
  priorCareerBonusYears?: number;

  /** 정규직 전환 가산 연수 */
  regularConversionBonusYears?: number;

  /** 신규 승진 가산 연수 */
  newPromotionBonusYears?: number;

  // 취소 정보 (V73)
  /** 취소 일시 */
  canceledAt?: Date;

  /** 취소한 사용자 ID */
  canceledBy?: string;

  /** 취소한 사용자명 */
  canceledByName?: string;

  /** 취소 사유 */
  cancelReason?: string;

  // 메타데이터
  /** 생성 일시 */
  createdAt?: Date;

  /** 수정 일시 */
  updatedAt?: Date;

  /** 발령 등록한 사용자 ID */
  createdBy?: string;

  /** 발령 등록한 사용자명 */
  createdByName?: string;

  /** 발령 수정한 사용자 ID */
  updatedBy?: string;
}

/**
 * 인사발령 생성 입력
 *
 * 새로운 인사발령을 생성할 때 필요한 데이터입니다.
 */
export interface CreatePersonnelAppointmentInput {
  /** 대상 직원 ID (필수) */
  employeeId: string;

  /** 변경 전 직위 ID (선택, 첫 발령이면 null 가능) */
  fromPositionId?: string | null;

  /** 변경 후 직위 ID (필수) */
  toPositionId: string;

  /** 발령일 (필수, YYYY-MM-DD) */
  effectiveDate: string;

  /** 발령 유형 (필수) */
  type: PersonnelAppointmentType;

  /** 발령 설명/메모 (선택) */
  note?: string;
}

/**
 * 인사발령 수정 입력
 *
 * 기존 인사발령을 수정할 때 필요한 데이터입니다.
 * 모든 필드가 선택적이며, 제공된 필드만 업데이트됩니다.
 */
export interface UpdatePersonnelAppointmentInput {
  /** 변경 전 직위 ID */
  fromPositionId?: string | null;

  /** 변경 후 직위 ID */
  toPositionId?: string;

  /** 발령일 (YYYY-MM-DD) */
  effectiveDate?: string;

  /** 발령 유형 */
  type?: PersonnelAppointmentType;

  /** 발령 설명/메모 */
  note?: string;
}

/**
 * Firestore 문서를 PersonnelAppointment 타입으로 변환
 *
 * Firestore의 `personnelAppointments` 컬렉션 문서를 PersonnelAppointment 인터페이스에 맞게 매핑합니다.
 *
 * @param doc Firestore QueryDocumentSnapshot
 * @returns PersonnelAppointment 객체
 *
 * @example
 * const snapshot = await getDocs(collection(db, 'personnelAppointments'));
 * const appointments = snapshot.docs.map(mapPersonnelAppointmentDoc);
 */
export function mapPersonnelAppointmentDoc(
  doc: QueryDocumentSnapshot<DocumentData>
): PersonnelAppointment {
  const data = doc.data();

  return {
    id: doc.id,
    employeeId: data.employeeId || '',
    employeeName: data.employeeName,
    fromPositionId: data.fromPositionId ?? null,
    toPositionId: data.toPositionId,
    fromPositionName: data.fromPositionName,
    toPositionName: data.toPositionName,
    fromTitleId: data.fromTitleId,
    toTitleId: data.toTitleId,
    fromTitleName: data.fromTitleName,
    toTitleName: data.toTitleName,
    effectiveDate: data.effectiveDate || '',
    type: data.type,
    appointmentType: data.appointmentType,
    status: data.status,
    isExecuted: data.isExecuted,
    note: data.note,
    reason: data.reason,
    priorCareerBonusYears: data.priorCareerBonusYears,
    regularConversionBonusYears: data.regularConversionBonusYears,
    newPromotionBonusYears: data.newPromotionBonusYears,
    canceledAt: data.canceledAt?.toDate?.() || data.canceledAt,
    canceledBy: data.canceledBy,
    canceledByName: data.canceledByName,
    cancelReason: data.cancelReason,
    createdAt: data.createdAt?.toDate?.() || data.createdAt,
    updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
    createdBy: data.createdBy,
    createdByName: data.createdByName,
    updatedBy: data.updatedBy,
  };
}

/**
 * PersonnelAppointment을 Firestore 문서 형식으로 변환
 *
 * PersonnelAppointment 객체를 Firestore에 저장할 수 있는 형식으로 변환합니다.
 *
 * @param appointment PersonnelAppointment 객체 (일부)
 * @returns Firestore 문서 데이터
 *
 * @example
 * const docData = mapPersonnelAppointmentToFirestore(appointment);
 * await setDoc(doc(db, 'personnelAppointments', id), docData);
 */
export function mapPersonnelAppointmentToFirestore(
  appointment: Partial<PersonnelAppointment>
): Record<string, any> {
  const data: Record<string, any> = {};

  if (appointment.employeeId !== undefined) data.employeeId = appointment.employeeId;
  if (appointment.fromPositionId !== undefined) data.fromPositionId = appointment.fromPositionId;
  if (appointment.toPositionId !== undefined) data.toPositionId = appointment.toPositionId;
  if (appointment.effectiveDate !== undefined) data.effectiveDate = appointment.effectiveDate;
  if (appointment.type !== undefined) data.type = appointment.type;
  if (appointment.note !== undefined) data.note = appointment.note;
  if (appointment.createdAt !== undefined) data.createdAt = appointment.createdAt;
  if (appointment.updatedAt !== undefined) data.updatedAt = appointment.updatedAt;
  if (appointment.createdBy !== undefined) data.createdBy = appointment.createdBy;
  if (appointment.updatedBy !== undefined) data.updatedBy = appointment.updatedBy;

  return data;
}

/**
 * ============================================
 * 발령 유형별 라벨 (UI 표시용)
 * ============================================
 */
export const PERSONNEL_APPOINTMENT_TYPE_LABELS: Record<PersonnelAppointmentType, string> = {
  promotion: '승진',
  demotion: '강등',
  transfer: '조직이동',
  initial: '최초발령',
  correction: '정보수정',
};

/**
 * V72: 임명 유형별 라벨 (UI 표시용)
 */
export const APPOINTMENT_TYPE_LABELS: Record<AppointmentType, string> = {
  EARLY_PROMOTION: '조기 진급',
  PROMOTION: '승진',
  POSITION_APPOINTMENT: '직위 임명',
  TITLE_APPOINTMENT: '직책 임명',
};

/**
 * V72: 발령 상태별 라벨 (UI 표시용)
 */
export const APPOINTMENT_STATUS_LABELS: Record<AppointmentStatus, string> = {
  PLANNED: '예약',
  EXECUTED: '실행됨',
  CANCELED: '취소됨',
};

/**
 * ============================================
 * 핵심 비즈니스 로직
 * ============================================
 *
 * **중요: Employee의 positionId/currentPositionStartDate와의 연계**
 *
 * 인사발령 이력(personnelAppointments)은 직원의 현재 직위 정보의 원천 데이터입니다.
 *
 * **원칙:**
 * - Employee.positionId = 가장 최신 발령의 toPositionId
 * - Employee.currentPositionStartDate = 가장 최신 발령의 effectiveDate
 * - "가장 최신" = effectiveDate 기준 내림차순 정렬 시 첫 번째 기록
 *
 * **동기화 시점:**
 * 1. 인사발령 생성 시 (createAppointment)
 * 2. 인사발령 수정 시 (updateAppointment)
 * 3. 인사발령 삭제 시 (deleteAppointment)
 *
 * **동기화 방법:**
 * - 해당 employeeId의 모든 발령 이력을 조회
 * - effectiveDate 기준 내림차순 정렬
 * - 가장 최근 발령의 toPositionId, effectiveDate를 Employee에 반영
 *
 * **예시:**
 * ```ts
 * // 직원 A의 발령 이력
 * [
 *   { effectiveDate: '2023-01-01', toPositionId: 'pos_manager' },  // 가장 최신
 *   { effectiveDate: '2020-01-01', toPositionId: 'pos_assistant' },
 *   { effectiveDate: '2017-01-01', toPositionId: 'pos_staff' },
 * ]
 *
 * // Employee 데이터
 * {
 *   positionId: 'pos_manager',
 *   currentPositionStartDate: '2023-01-01'
 * }
 * ```
 *
 * 이 로직은 usePersonnelAppointmentMutations Hook에서 구현됩니다.
 */

/**
 * ============================================
 * TODO: 조직 이동(transfer) 타입 확장
 * ============================================
 *
 * 현재는 직위 변경 위주로 구현되어 있습니다.
 * 향후 조직 이동 기능을 추가할 경우:
 *
 * 1. **필드 추가:**
 *    - fromOrgUnitId?: string | null
 *    - toOrgUnitId?: string
 *
 * 2. **Employee 동기화 확장:**
 *    - orgUnitId도 가장 최신 발령 기준으로 동기화
 *
 * 3. **UI 개선:**
 *    - 발령 유형이 transfer일 때 조직 선택 필드 표시
 *    - 타임라인에 조직 이동 정보 함께 표시
 *
 * 4. **조직도 연계:**
 *    - 조직 이동 시 조직도 실시간 업데이트
 *    - 이전/이후 조직 인원 수 자동 조정
 */

/**
 * ============================================
 * TODO: 발령 승인 Workflow (향후 구현)
 * ============================================
 *
 * 현재는 발령 등록 즉시 반영됩니다.
 * 향후 승인 프로세스를 추가할 경우:
 *
 * 1. **상태 필드 추가:**
 *    - status: 'draft' | 'pending' | 'approved' | 'rejected'
 *    - approvedBy?: string
 *    - approvedAt?: Date
 *    - rejectedReason?: string
 *
 * 2. **승인 로직:**
 *    - 발령 생성 시 status='pending'
 *    - 승인자가 approve() 호출 → status='approved', Employee 동기화
 *    - 반려 시 reject() 호출 → status='rejected', Employee 동기화 안 함
 *
 * 3. **UI 개선:**
 *    - 발령 대기 목록 (pending)
 *    - 승인/반려 버튼
 *    - 승인 이력 표시
 */

/**
 * ============================================
 * TODO: 대량 발령 처리 (엑셀 업로드)
 * ============================================
 *
 * 정기 인사발령 시 많은 직원을 한 번에 처리해야 하는 경우:
 *
 * 1. **엑셀 업로드 기능:**
 *    - 발령 대상자 목록을 엑셀로 작성
 *    - 업로드 시 일괄 생성
 *    - Firestore Batch Write 사용 (최대 500건)
 *
 * 2. **템플릿 제공:**
 *    - 다운로드 가능한 엑셀 템플릿
 *    - 필수/선택 필드 가이드
 *
 * 3. **유효성 검증:**
 *    - 직원 ID 존재 여부
 *    - 직위 ID 유효성
 *    - 날짜 형식 검증
 *    - 중복 발령 체크
 */
